import { ActionContext, ActionTree } from "vuex";
import { StatusCodes } from "http-status-codes";
import {
  ICoreErrorResponse,
  ICoreResponse,
} from "@/core/interfaces/coreResponse.interface";

import DepartmentService from "../services/department.service";
import { IDepartmentState } from "../types";
import { StoreState } from "@/store/type";
import notificationService from "@/core/services/notification.service";
import { NOTIFICATION_TYPES } from "@/core/enums/NOTIFICATION_TYPES";
import { NOTIFICATION_MESSAGES } from "@/core/constants/notificationMessages";
import { formatString } from "@/core/utils/helpers";
import analyticsService from "@/analytic.service";
import { DEPARTMENT_ANALYTICS_EVENTS } from "../constants/analyticsEvents";
import {
  CreateDepartmentDto,
  UpdateDepartmentDto,
} from "../dtos/department.dtos";

const departmentService = new DepartmentService();

const actions: ActionTree<IDepartmentState, StoreState> = {
  async fetchDepartments(
    context: ActionContext<IDepartmentState, StoreState>,
    payload: { organizationId: string; isActive?: boolean },
  ) {
    try {
      context.commit("setFetchingDepartments", true);
      const { organizationId, isActive } = payload;
      const response: ICoreResponse = await departmentService.getDepartments(
        organizationId,
        isActive,
      );
      if (response.success && response.status === StatusCodes.OK) {
        context.commit("setDepartments", response.data);
        analyticsService.logActionEvent(
          DEPARTMENT_ANALYTICS_EVENTS.DEPARTMENT_FETCH,
          { organizationId, isActive },
        );
      } else {
        throw response;
      }
    } catch (error: unknown) {
      context.commit("setDepartments", []);
    } finally {
      context.commit("setFetchingDepartments", false);
    }
  },

  async createDepartment(
    context: ActionContext<IDepartmentState, StoreState>,
    {
      organizationId,
      body,
      successHandler,
      errorHandler,
    }: {
      organizationId: string;
      body: CreateDepartmentDto;
      successHandler: () => void;
      errorHandler?: (error: ICoreErrorResponse) => void;
    },
  ) {
    try {
      context.commit("setDepartmentActionLoading", true);
      const response: ICoreResponse = await departmentService.createDepartment(
        organizationId,
        body,
      );
      if (
        response.success &&
        (response.status === StatusCodes.OK ||
          response.status === StatusCodes.CREATED)
      ) {
        successHandler();
        analyticsService.logActionEvent(
          DEPARTMENT_ANALYTICS_EVENTS.DEPARTMENT_CREATE,
          { organizationId, name: body.name },
        );
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          response.message || NOTIFICATION_MESSAGES.DEPARTMENT_CREATE_SUCCESS;
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      if (errorHandler) {
        errorHandler(exception);
      }
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      context.commit("setDepartmentActionLoading", false);
      notificationService.triggerNotification();
    }
  },

  async updateDepartment(
    context: ActionContext<IDepartmentState, StoreState>,
    {
      organizationId,
      body,
      successHandler,
      errorHandler,
    }: {
      organizationId: string;
      body: UpdateDepartmentDto;
      successHandler: () => void;
      errorHandler?: (error: ICoreErrorResponse) => void;
    },
  ) {
    try {
      context.commit("setDepartmentActionLoading", true);
      const response: ICoreResponse = await departmentService.updateDepartment(
        organizationId,
        body,
      );
      if (response.success && response.status === StatusCodes.OK) {
        successHandler();
        analyticsService.logActionEvent(
          DEPARTMENT_ANALYTICS_EVENTS.DEPARTMENT_UPDATE,
          { organizationId, publicId: body.public_id, name: body.name },
        );
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          response.message || NOTIFICATION_MESSAGES.DEPARTMENT_UPDATE_SUCCESS;
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      if (errorHandler) {
        errorHandler(exception);
      }
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      context.commit("setDepartmentActionLoading", false);
      notificationService.triggerNotification();
    }
  },

  async deleteDepartment(
    context: ActionContext<IDepartmentState, StoreState>,
    {
      organizationId,
      publicId,
      successHandler,
    }: {
      organizationId: string;
      publicId: string;
      successHandler: () => void;
    },
  ) {
    try {
      context.commit("setDepartmentActionLoading", true);
      const response: ICoreResponse = await departmentService.deleteDepartment(
        organizationId,
        publicId,
      );
      if (response.success && response.status === StatusCodes.OK) {
        const updatedDepartments = context.state.departments.filter(
          (dept) => dept.public_id !== publicId,
        );
        context.commit("setDepartments", updatedDepartments);
        successHandler();
        analyticsService.logActionEvent(
          DEPARTMENT_ANALYTICS_EVENTS.DEPARTMENT_DELETE,
          { organizationId, publicId },
        );
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          response.message || NOTIFICATION_MESSAGES.DEPARTMENT_DELETE_SUCCESS;
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      context.commit("setDepartmentActionLoading", false);
      notificationService.triggerNotification();
    }
  },

  async patchDepartmentStatus(
    context: ActionContext<IDepartmentState, StoreState>,
    {
      organizationId,
      departmentId,
      departmentName,
      isActive,
      successHandler,
      errorHandler,
    }: {
      organizationId: string;
      departmentId: string;
      departmentName: string;
      isActive: boolean;
      successHandler?: () => void;
      errorHandler?: () => void;
    },
  ) {
    try {
      context.commit("setDepartmentActionLoading", true);
      const response: ICoreResponse =
        await departmentService.patchDepartmentStatus(
          organizationId,
          departmentId,
          isActive,
        );
      if (response.success && response.status === StatusCodes.OK) {
        const updatedDepartments = context.state.departments.map((dept) => {
          if (dept.public_id === departmentId) {
            return { ...dept, is_active: isActive };
          }
          return dept;
        });
        context.commit("setDepartments", updatedDepartments);
        if (successHandler) {
          successHandler();
        }
        analyticsService.logActionEvent(
          DEPARTMENT_ANALYTICS_EVENTS.DEPARTMENT_STATUS_TOGGLE,
          { organizationId, departmentId, departmentName, isActive },
        );
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description = formatString(
          NOTIFICATION_MESSAGES.DEPARTMENT_STATUS_UPDATE_SUCCESS,
          departmentName,
          isActive ? "active" : "inactive",
        );
      } else {
        throw response;
      }
    } catch (error: unknown) {
      if (errorHandler) {
        errorHandler();
      }
      const exception = error as ICoreErrorResponse;
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      context.commit("setDepartmentActionLoading", false);
      notificationService.triggerNotification();
    }
  },
};

export default actions;
